import React from 'react';
import { FaSearch } from 'react-icons/fa';
import { formatDateTime, currencyRounded } from '../../utils/redemptionHelpers';
import RedemptionStatusBadge from './RedemptionStatusBadge';
import Pagination from '../common/Pagination';

const RedemptionHistoryTable = ({
  redemptions,
  redemptionSearchInput,
  setRedemptionSearchInput,
  handleRedemptionSearchSubmit,
  redemptionStatus,
  setRedemptionStatus,
  redemptionPage,
  redemptionTotalPages,
  redemptionPagination,
  setRedemptionPage,
  currentRole,
  approvingRequests,
  approvedRequestIds,
  handleApproveRequest
}) => {
  const isPendingOrInProgress = (status) => {
    const statusLower = String(status).toLowerCase();
    return statusLower === 'pending' || 
           statusLower === 'in progress' || 
           statusLower === 'in_progress' ||
           statusLower.includes('pending') ||
           statusLower.includes('progress');
  };

  const canApprove = (status, requestId) => {
    if (currentRole !== 'Finance' && currentRole !== 'SuperAdmin') return false;
    if (approvedRequestIds.has(requestId)) return false;
    const statusLower = String(status).toLowerCase();
    if (statusLower === 'approved' || statusLower === 'success' || statusLower === 'successful') return false;
    return isPendingOrInProgress(status);
  };

  const formatRedemptionDateTime = (r) => {
    let dateTimeValue = r.date_time || r.requested_at || r.actioned_at || r.date || r.requestedAt || r.created_at || r.timestamp;
    if (typeof dateTimeValue === 'object' && dateTimeValue !== null && !(dateTimeValue instanceof Date)) {
      if (dateTimeValue.start_date && dateTimeValue.end_date) {
        let start = dateTimeValue.start_date;
        let end = dateTimeValue.end_date;
        
        if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
          start = formatDateTime(start);
        } else {
          start = formatDateTime(String(start));
        }
        
        if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
          end = formatDateTime(end);
        } else {
          end = formatDateTime(String(end));
        }
        
        return `${start} to ${end}`;
      } else {
        return 'N/A';
      }
    } else {
      return formatDateTime(dateTimeValue || 'N/A');
    }
  };

  return (
    <div className="bg-white rounded-md shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">Redemption History</span>
          </h2>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {/* Search and Filter Controls */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={redemptionSearchInput}
              onChange={(e) => setRedemptionSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRedemptionSearchSubmit();
                }
              }}
              placeholder="Search by Request ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={redemptionStatus || 'all'}
            onChange={(e) => {
              if (setRedemptionStatus) {
                setRedemptionStatus(e.target.value === 'all' ? '' : e.target.value);
              }
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Requested</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Approved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved by</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Account</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {redemptions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No redemption requests found
                  </td>
                </tr>
              ) : (
                redemptions.map((r) => {
                  const statusValue = r.status || 'N/A';
                  const requestId = r.request_id || r.redemption_id || r.id;
                  const statusLower = String(statusValue).toLowerCase();
                  const isApproving = requestId && approvingRequests.has(requestId);
                  const actionedAt = r.actioned_at || r.actionedAt || r.actioned_at_time;
                  const hasActionedAt = actionedAt && 
                    typeof actionedAt !== 'object' && 
                    String(actionedAt).trim() !== '';
                  const isApproved = approvedRequestIds.has(requestId) || 
                    statusLower === 'approved' || 
                    statusLower === 'success' || 
                    statusLower === 'successful';
                  
                  return (
                    <tr key={requestId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatRedemptionDateTime(r)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {currencyRounded(r.amount_requested || r.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {r.amount_approved !== undefined && r.amount_approved !== null 
                          ? currencyRounded(r.amount_approved) 
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RedemptionStatusBadge status={statusValue} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.actioned_by && r.actioned_by.trim() !== '' 
                          ? r.actioned_by 
                          : (r.approved_by || r.approvedBy || r.approved_by_name || r.approved_by_user || r.approvals || '-')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.bank_account || r.bank || r.bank_account_number || r.account_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {hasActionedAt || isApproved ? (
                          <span className="text-green-600 font-medium">Approved</span>
                        ) : canApprove(statusValue, requestId) ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveRequest(requestId);
                            }}
                            disabled={isApproving}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              isApproving
                                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                          >
                            {isApproving ? 'Approving...' : 'Approve'}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {requestId || 'N/A'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={redemptionPage}
          totalPages={redemptionTotalPages}
          totalItems={redemptionPagination.total}
          startIdx={(redemptionPage - 1) * redemptionPagination.items}
          pageSize={redemptionPagination.items}
          onPageChange={setRedemptionPage}
        />
      </div>
    </div>
  );
};

export default RedemptionHistoryTable;

